// External libraries
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Zocdoc libraries
import { grey } from '@zocdoc/branding/colors';
import {
    Cancelled,
    CheckIn,
    Confirmed,
    NeedInfo,
    Rescheduled,
    Star,
    Zee,
} from '@zocdoc-frontend-common/assets-icons';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import {
    MetricsClickable,
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc/frontend-metrics';
import { NOTIFICATION_TYPE } from '@zocdoc-frontend-common/state-user-account/constants';

// This package
import { DropdownContainer } from '../dropdown';
import { NotificationTime } from './NotificationTime';

const NotificationContainer = styled.div`
    padding-top: 8px;
    display: flex;
    justify-content: flex-start;
`;

const IconContainer = styled.div`
    margin: 4px 18px 10px 5px;
    min-width: 40px;
    color: inherit;
`;

const Text = styled.div`
    display: block;
    font: 16px/20px ${FontFamily.KievitSlabBook};
`;

const Time = styled.div`
    display: block;
    font: 13px/20px ${FontFamily.SharpSansSemibold};
    color: ${grey};
    font-variant-numeric: lining-nums;
`;

const defaultNotificationUrl = '/patient/medicalteam';

export class Notification extends Component {
    static propTypes = {
        notification: PropTypes.shape({
            id: PropTypes.any.isRequired,
            seen: PropTypes.bool.isRequired,
            text: PropTypes.string.isRequired,
            timestamp: PropTypes.number.isRequired,
            type: PropTypes.oneOf(Object.values(NOTIFICATION_TYPE)).isRequired,
            url: PropTypes.string,
        }),
        onNotificationClick: PropTypes.func,
        sendMetric: PropTypes.func,
    };

    static defaultProps = {
        sendMetric: () => {},
    };

    componentDidMount() {
        this.props.sendMetric({
            initiator: EVENT_INITIATORS.SYSTEM,
            interactionType: INTERACTION_TYPES.LOAD,
            ...this.metricsData(),
        });
    }

    render() {
        const { notification, onNotificationClick } = this.props;

        const { id, seen, text, timestamp, type } = notification;

        const url = notification.url || defaultNotificationUrl;

        if (text) {
            // cleanup html tags from text
            const cleanText = text.replace(/<\/?[^>]+(>|$)/g, '');
            return (
                <DropdownContainer
                    highlight={!seen}
                    href={url}
                    itemId={id}
                    onClick={onNotificationClick}
                >
                    <MetricsClickable
                        action="Click PageHeader Notifications Notification"
                        {...this.metricsData()}
                    >
                        <NotificationContainer>
                            <IconContainer>{getIcon(type)}</IconContainer>
                            <div>
                                <Text
                                    className="exclude-from-playback"
                                    data-test="notification-text"
                                >
                                    {cleanText}
                                </Text>
                                <Time data-test="notification-time">
                                    <NotificationTime timestamp={timestamp} />
                                </Time>
                            </div>
                        </NotificationContainer>
                    </MetricsClickable>
                </DropdownContainer>
            );
        } else {
            return <div />;
        }
    }

    metricsData = () => {
        const { notification } = this.props;
        const { text, timestamp, type } = notification;

        return {
            element: 'Notification',
            eventDetails: {
                NotificationType: type,
                Message: text,
                Timestamp: timestamp,
            },
        };
    };
}

// Icon heights are all different because the requirement is to have their widths be the same (40px)
// and we implemented Icon to not take width as a parameter :(
function getIcon(type) {
    switch (type) {
        case NOTIFICATION_TYPE.ZocdocMessage:
            return <Zee color="currentColor" expression="smile" height={50} />;
        case NOTIFICATION_TYPE.AppointmentConfirmed:
            return <Confirmed color="currentColor" height={42.11} />;
        case NOTIFICATION_TYPE.AppointmentCancelled:
        case NOTIFICATION_TYPE.AppointmentAsyncBookingFailure:
            return <Cancelled color="currentColor" height={42.11} />;
        case NOTIFICATION_TYPE.AppointmentRescheduled:
            return <Rescheduled color="currentColor" height={42.11} />;
        case NOTIFICATION_TYPE.AppointmentInfoNeeded:
            return <NeedInfo color="currentColor" height={42.11} />;
        case NOTIFICATION_TYPE.AppointmentFeedback:
            return <Star color="currentColor" height={37.21} />;
        case NOTIFICATION_TYPE.AppointmentCheckIn:
            return <CheckIn color="currentColor" height={37.21} />;
        // no default
    }
}
